/**
 * Floating Call Window
 * Minimized draggable video call window
 */
import React, { useState, useRef, useEffect } from 'react';
import { useVideoCall, CallStatus } from '../../context/VideoCallContext';
import VideoCallControls from './VideoCallControls';
import { Loader2 } from 'lucide-react';

const FloatingCallWindow = () => {
  const { 
    callState, 
    mediaState,
    formatDuration
  } = useVideoCall();

  const [position, setPosition] = useState({ x: 20, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  const { status, duration, participants, isMinimized } = callState;
  const { localStream, remoteStream } = mediaState;

  // Get the other participant
  const otherParticipant = participants.find(p => !p.is_caller) || participants[1];

  // Handle drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      setPosition({
        x: e.clientX - offsetRef.current.x,
        y: e.clientY - offsetRef.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      
      setPosition({
        x: touch.clientX - offsetRef.current.x,
        y: touch.clientY - offsetRef.current.y
      });
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  const handleDragStart = (e) => {
    const rect = dragRef.current.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    offsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
    
    setIsDragging(true);
  };

  // Don't render if not in a call or not minimized
  if (!isMinimized || status === CallStatus.IDLE || status === CallStatus.ENDED) {
    return null;
  }

  return (
    <div
      ref={dragRef}
      className="fixed z-[9999] select-none"
      style={{
        left: position.x,
        top: position.y,
        touchAction: 'none'
      }}
      data-testid="floating-call-window"
    >
      <div className="w-40 bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10">
        {/* Video preview */}
        <div 
          className="relative h-48 bg-gray-900 cursor-move"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          {remoteStream ? (
            <video
              autoPlay
              playsInline
              muted={false}
              className="w-full h-full object-cover"
              ref={(video) => {
                if (video && remoteStream) {
                  video.srcObject = remoteStream;
                }
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {status === CallStatus.CALLING && (
                <div className="text-center text-white">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-xs">Calling...</p>
                </div>
              )}
              {status === CallStatus.CONNECTING && (
                <div className="text-center text-white">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-xs">Connecting...</p>
                </div>
              )}
            </div>
          )}

          {/* Local video pip */}
          {localStream && (
            <div className="absolute bottom-2 right-2 w-12 h-16 rounded-lg overflow-hidden border-2 border-white/30">
              <video
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover mirror"
                ref={(video) => {
                  if (video && localStream) {
                    video.srcObject = localStream;
                  }
                }}
              />
            </div>
          )}

          {/* Status overlay */}
          <div className="absolute top-2 left-2 right-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white bg-black/50 px-2 py-1 rounded-full truncate max-w-20">
                {otherParticipant?.name || 'Unknown'}
              </span>
              {status === CallStatus.CONNECTED && (
                <span className="text-xs text-white bg-green-500/80 px-2 py-1 rounded-full font-mono">
                  {formatDuration(duration)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Mini controls */}
        <div className="p-2 bg-black/90">
          <VideoCallControls className="p-2 bg-transparent scale-75 origin-center" />
        </div>
      </div>
    </div>
  );
};

export default FloatingCallWindow;
